import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, Shield, Info, AlertCircle, Mail, Phone, MapPin, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Función para el desplazamiento suave a los anclajes
const scrollToAnchor = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Componente de Tabla de Contenidos
const TableOfContents = ({ t }) => (
  <div className="sticky top-24 bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-8">
    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
      <span className="mr-2">📋</span> {t('privacyPolicy.tocTitle')}
    </h2>
    <nav>
      <ul className="space-y-2">
        {[
          { id: 'datos-recabados', label: t('privacyPolicy.toc.datosRecabados') },
          { id: 'finalidades', label: t('privacyPolicy.toc.finalidades') },
          { id: 'transferencias', label: t('privacyPolicy.toc.transferencias') },
          { id: 'derechos-arco', label: t('privacyPolicy.toc.derechosArco') },
          { id: 'limitacion-uso', label: t('privacyPolicy.toc.limitacionUso') },
          { id: 'cookies', label: t('privacyPolicy.toc.cookies') },
          { id: 'cambios', label: t('privacyPolicy.toc.cambios') },
          { id: 'contacto', label: t('privacyPolicy.toc.contacto') },
        ].map((item) => (
          <li key={item.id}>
            <button
              onClick={() => scrollToAnchor(item.id)}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center group w-full text-left"
            >
              <ChevronRight className="h-3 w-3 mr-2 text-blue-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  </div>
);

// Componente de Sección
const Section = ({ id, title, children, icon: Icon = Info, className = '' }) => (
  <section id={id} className={`mb-12 scroll-mt-20 ${className}`}>
    <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
      {Icon && <Icon className="h-6 w-6 text-blue-600 mr-2" />}
      {title}
    </h2>
    {children}
  </section>
);

const AvisoPrivacidad = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => scrollToAnchor(id), 100);
    }
  }, [location]);

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Encabezado */}
      <header className="relative overflow-hidden text-white shadow-lg">
        {/* Fondo con patrón */}
        <div className="absolute inset-0 hero-pattern backdrop-blur-sm" />
        {/* Capa semitransparente para mejorar contraste */}
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <Link
                to="/"
                className="inline-flex items-center text-blue-100 hover:text-white mb-4 md:mb-0 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-1" />
                {t('privacyPolicy.backHome')}
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold mt-2">{t('privacyPolicy.title')}</h1>
              <p className="text-blue-100 mt-2">
                {t('privacyPolicy.lastUpdate')}
              </p>
            </div>
            <div className="mt-6 md:mt-0">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-700 bg-opacity-50 text-sm font-medium">
                <Shield className="h-4 w-4 mr-2" /> {t('privacyPolicy.documentInfo')}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Barra lateral */}
          <div className="lg:w-1/4">
            <div className="sticky top-24 space-y-6">
              <TableOfContents t={t} />

              <div className="hidden lg:block bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-3 flex items-center">
                  <Info className="h-5 w-5 mr-2" /> {t('privacyPolicy.helpTitle')}
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  {t('privacyPolicy.helpDescription')}
                </p>
                <Link
                  to="/contacto"
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  {t('privacyPolicy.contactLink')} <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
              <Section id="introduccion" title={t('privacyPolicy.sections.introduccion.title')} icon={FileText}>
                <p className="text-gray-600 mb-6">
                  {t('privacyPolicy.sections.introduccion.lastUpdateLabel')}
                </p>
                <p className="mb-4">
                  {t('privacyPolicy.sections.introduccion.paragraph1')}
                </p>
                <p>
                  {t('privacyPolicy.sections.introduccion.paragraph2')}
                </p>
              </Section>

              <Section id="datos-recabados" title={t('privacyPolicy.sections.datosRecabados.title')}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.datosRecabados.paragraph1')}
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.datosRecabados.list.item1') }}></li>
                  <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.datosRecabados.list.item2') }}></li>
                </ul>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                  <p className="text-sm text-blue-700">
                    <strong dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.datosRecabados.note').split(':')[0] }}></strong>: {t('privacyPolicy.sections.datosRecabados.note').split(':')[1]}
                  </p>
                </div>
              </Section>

              <Section id="finalidades" title={t('privacyPolicy.sections.finalidades.title')}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.finalidades.paragraph1')}
                </p>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('privacyPolicy.sections.finalidades.primarias.title')}</h3>
                  <p className="mb-3">
                    {t('privacyPolicy.sections.finalidades.primarias.paragraph')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mb-4">
                    <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.finalidades.primarias.list.item1') }}></li>
                    <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.finalidades.primarias.list.item2') }}></li>
                    <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.finalidades.primarias.list.item3') }}></li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('privacyPolicy.sections.finalidades.secundarias.title')}</h3>
                  <p className="mb-3">
                    {t('privacyPolicy.sections.finalidades.secundarias.paragraph')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.finalidades.secundarias.list.item1') }}></li>
                    <li dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.finalidades.secundarias.list.item2') }}></li>
                  </ul>
                  <p className="mt-3 text-sm">
                    {t('privacyPolicy.sections.finalidades.secundarias.note')}
                  </p>
                </div>
              </Section>

              <Section id="transferencias" title={t('privacyPolicy.sections.transferencias.title')}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.transferencias.paragraph1')}
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>{t('privacyPolicy.sections.transferencias.list.item1')}</li>
                  <li>{t('privacyPolicy.sections.transferencias.list.item2')}</li>
                </ul>
                <p className="mb-4">
                  {t('privacyPolicy.sections.transferencias.paragraph2')}
                </p>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                  <p className="text-blue-700">
                    <strong dangerouslySetInnerHTML={{ __html: t('privacyPolicy.sections.transferencias.note').split(':')[0] }}></strong>: {t('privacyPolicy.sections.transferencias.note').split(':')[1]}
                  </p>
                </div>
              </Section>

              <Section id="derechos-arco" title={t('privacyPolicy.sections.derechosArco.title')}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.derechosArco.paragraph1')}
                </p>
                <p className="mb-4">
                  {t('privacyPolicy.sections.derechosArco.paragraph2')}
                </p>
                <p className="mb-4">
                  {t('privacyPolicy.sections.derechosArco.paragraph3')}
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>{t('privacyPolicy.sections.derechosArco.contact.email')}</li>
                  <li>{t('privacyPolicy.sections.derechosArco.contact.phone')}</li>
                </ul>
                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400 mb-4">
                  <p className="text-yellow-700 font-medium mb-2">{t('privacyPolicy.sections.derechosArco.requestInfo.title')}</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>{t('privacyPolicy.sections.derechosArco.requestInfo.list.item1')}</li>
                    <li>{t('privacyPolicy.sections.derechosArco.requestInfo.list.item2')}</li>
                    <li>{t('privacyPolicy.sections.derechosArco.requestInfo.list.item3')}</li>
                    <li>{t('privacyPolicy.sections.derechosArco.requestInfo.list.item4')}</li>
                  </ul>
                </div>
                <p>
                  {t('privacyPolicy.sections.derechosArco.response')}
                </p>
              </Section>

              <Section id="limitacion-uso" title={t('privacyPolicy.sections.limitacionUso.title')}>
                <p>
                  {t('privacyPolicy.sections.limitacionUso.paragraph')}
                </p>
              </Section>

              <Section id="cookies" title={t('privacyPolicy.sections.cookies.title')}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.cookies.paragraph')}
                </p>
              </Section>

              <Section id="cambios" title={t('privacyPolicy.sections.cambios.title')} icon={AlertCircle}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.cambios.paragraph1')}
                </p>
                <p>
                  {t('privacyPolicy.sections.cambios.paragraph2')}
                </p>
              </Section>

              <Section id="contacto" title={t('privacyPolicy.sections.contacto.title')} icon={Mail}>
                <p className="mb-4">
                  {t('privacyPolicy.sections.contacto.paragraph1')}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Mail className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyPolicy.sections.contacto.info.email')}</span>
                  </li>
                  <li className="flex items-start">
                    <Phone className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyPolicy.sections.contacto.info.phone')}</span>
                  </li>
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyPolicy.sections.contacto.info.address')}</span>
                  </li>
                </ul>
                <p className="mt-6 text-sm text-gray-500">
                  <strong>{t('privacyPolicy.sections.contacto.hours').split(':')[0]}:</strong> {t('privacyPolicy.sections.contacto.hours').split(':')[1]}
                </p>
              </Section>

              <div className="mt-12 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center">
                  {t('privacyPolicy.footer.copy', { year: new Date().getFullYear() })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </motion.div>
  );
};

export default AvisoPrivacidad;
